/**
 * ShopAnalyticsView - Analytics dashboard for Shop administration
 * Extracted from AdminShopTab.js in Phase 4D-3
 * 
 * Displays:
 * - Revenue (30 days)
 * - Order count (30 days)
 * - Average order value
 * - Low stock items count
 * - Top products list
 * - Low stock alert list
 */
import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { formatCurrency } from '../../../utils/format';

/**
 * @param {Object} props
 * @param {Object|null} props.analytics - Analytics data from backend
 * @param {number} props.analytics.total_revenue - Total revenue in last 30 days
 * @param {number} props.analytics.total_orders - Total orders in last 30 days
 * @param {number} props.analytics.average_order_value - Average order value
 * @param {Array} props.analytics.top_products - Top selling products
 * @param {Array} props.analytics.low_stock_products - Products with low stock
 */
export function ShopAnalyticsView({ analytics }) {
  // Don't render if no analytics data
  if (!analytics) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Revenue Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="text-2xl font-bold text-slate-900">
            {formatCurrency(analytics.total_revenue)}
          </div>
          <p className="text-sm text-slate-500">Revenue (30 days)</p>
        </CardContent>
      </Card>

      {/* Orders Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="text-2xl font-bold text-slate-900">
            {analytics.total_orders}
          </div>
          <p className="text-sm text-slate-500">Orders (30 days)</p>
        </CardContent>
      </Card>

      {/* Average Order Value Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="text-2xl font-bold text-slate-900">
            {formatCurrency(analytics.average_order_value)}
          </div>
          <p className="text-sm text-slate-500">Avg. Order Value</p>
        </CardContent>
      </Card>

      {/* Low Stock Count Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="text-2xl font-bold text-slate-900">
            {analytics.low_stock_products?.length || 0}
          </div>
          <p className="text-sm text-slate-500">Low Stock Items</p>
        </CardContent>
      </Card>
      
      {/* Top Products */}
      {analytics.top_products?.length > 0 && (
        <Card className="col-span-full lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Top Products</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.top_products.map((product, idx) => (
                <div key={product.product_id} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-slate-400">{idx + 1}</span>
                    <span className="font-medium">{product.title}</span>
                  </div>
                  <span className="text-sm text-slate-600">{product.sales_count || 0} sold</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Low Stock Alert */}
      {analytics.low_stock_products?.length > 0 && (
        <Card className="col-span-full lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Low Stock Alert
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.low_stock_products.map((product) => (
                <div key={product.product_id} className="flex items-center justify-between">
                  <span className="font-medium">{product.title}</span>
                  <Badge variant="destructive">{product.stock_quantity} left</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default ShopAnalyticsView;
